import type { LayoutHotspotListItem, OpenIssueCounts } from '../types/types';

export interface HeatMapSummary {
  label: string;
  counts: OpenIssueCounts;
  photoCount: number;
  hotspotCount: number;
}

export const EMPTY_ISSUE_COUNTS: OpenIssueCounts = {
  observations: 0,
  punch_items: 0,
  rfis: 0,
  submittals: 0,
  safety: 0,
  total: 0,
};

export const HEAT_MAP_STOPS = [
  {
    label: '0 open issues',
    range: '0',
    color: 'var(--heatmap-none)',
    softColor: 'var(--heatmap-none-soft)',
  },
  {
    label: '1-5 open issues',
    range: '1-5',
    color: 'var(--heatmap-low)',
    softColor: 'var(--heatmap-low-soft)',
  },
  {
    label: '6-10 open issues',
    range: '6-10',
    color: 'var(--heatmap-medium)',
    softColor: 'var(--heatmap-medium-soft)',
  },
  {
    label: '11+ open issues',
    range: '11+',
    color: 'var(--heatmap-high)',
    softColor: 'var(--heatmap-high-soft)',
  },
] as const;

export type HeatMapIssueRange = 'all' | (typeof HEAT_MAP_STOPS)[number]['range'];

export const addIssueCounts = (a: OpenIssueCounts, b: OpenIssueCounts): OpenIssueCounts => ({
  observations: a.observations + b.observations,
  punch_items: a.punch_items + b.punch_items,
  rfis: a.rfis + b.rfis,
  submittals: a.submittals + b.submittals,
  safety: a.safety + b.safety,
  total: a.total + b.total,
});

const HEAT_MAP_RGB_STOPS = {
  none: [34, 197, 94],
  low: [250, 204, 21],
  medium: [249, 115, 22],
  high: [239, 68, 68],
} as const;

const interpolateRgb = (
  from: readonly [number, number, number],
  to: readonly [number, number, number],
  amount: number
) => {
  const t = Math.max(0, Math.min(1, amount));
  return from.map((channel, index) => Math.round(channel + (to[index] - channel) * t)) as [
    number,
    number,
    number,
  ];
};

const getHeatMapRgb = (count: number): [number, number, number] => {
  if (count <= 0) return [...HEAT_MAP_RGB_STOPS.none];
  if (count <= 5) return interpolateRgb(HEAT_MAP_RGB_STOPS.none, HEAT_MAP_RGB_STOPS.low, count / 5);
  if (count <= 10) return interpolateRgb(HEAT_MAP_RGB_STOPS.low, HEAT_MAP_RGB_STOPS.medium, (count - 5) / 5);
  if (count < 11) return interpolateRgb(HEAT_MAP_RGB_STOPS.medium, HEAT_MAP_RGB_STOPS.high, count - 10);
  return [...HEAT_MAP_RGB_STOPS.high];
};

export const getHeatMapColor = (count: number): string => {
  const [r, g, b] = getHeatMapRgb(count);
  return `rgb(${r}, ${g}, ${b})`;
};

export const getHeatMapSoftColor = (count: number): string => {
  const [r, g, b] = getHeatMapRgb(count);
  const alpha = count <= 0 ? 0.16 : count >= 11 ? 0.22 : 0.16 + Math.min(count, 10) * 0.006;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const getHeatMapLabelColor = (count: number): string => {
  return count > 0 && count <= 5 ? 'var(--foreground)' : 'var(--primary-foreground)';
};

export const issueCountMatchesRange = (count: number, range: HeatMapIssueRange): boolean => {
  if (range === 'all') return true;
  if (range === '0') return count <= 0;
  if (range === '1-5') return count >= 1 && count <= 5;
  if (range === '6-10') return count >= 6 && count <= 10;
  return count >= 11;
};

export const getIssueSummaryText = (summary?: HeatMapSummary | null): string => {
  if (!summary) return '';
  const { counts } = summary;
  return [
    `${summary.label}: ${counts.total} open issue${counts.total === 1 ? '' : 's'}`,
    `Observations: ${counts.observations}`,
    `Punch items: ${counts.punch_items}`,
    `RFIs: ${counts.rfis}`,
    `Submittals: ${counts.submittals}`,
    `Safety: ${counts.safety}`,
  ].join('\n');
};

export const buildPhotoIssueCounts = (
  hotspots: LayoutHotspotListItem[]
): Map<number, OpenIssueCounts> => {
  const countsByPhoto = new Map<number, OpenIssueCounts>();

  hotspots.forEach((hotspot) => {
    const photoId = Number(hotspot.photo_id);
    if (!Number.isFinite(photoId)) return;
    const current = countsByPhoto.get(photoId) ?? EMPTY_ISSUE_COUNTS;
    countsByPhoto.set(photoId, addIssueCounts(current, hotspot.open_issue_counts ?? EMPTY_ISSUE_COUNTS));
  });

  return countsByPhoto;
};

export const buildPhotoHotspotCounts = (
  hotspots: LayoutHotspotListItem[]
): Map<number, number> => {
  const countsByPhoto = new Map<number, number>();

  hotspots.forEach((hotspot) => {
    const photoId = Number(hotspot.photo_id);
    if (!Number.isFinite(photoId)) return;
    countsByPhoto.set(photoId, (countsByPhoto.get(photoId) ?? 0) + 1);
  });

  return countsByPhoto;
};

export const summarizePhotoIds = (
  photoIds: Iterable<number>,
  countsByPhoto: Map<number, OpenIssueCounts>,
  hotspotsByPhoto: Map<number, number>,
  label: string
): HeatMapSummary => {
  let counts = EMPTY_ISSUE_COUNTS;
  let hotspotCount = 0;
  const uniquePhotoIds = new Set(Array.from(photoIds).map(Number).filter(Number.isFinite));
  uniquePhotoIds.forEach((photoId) => {
    counts = addIssueCounts(counts, countsByPhoto.get(photoId) ?? EMPTY_ISSUE_COUNTS);
    hotspotCount += hotspotsByPhoto.get(photoId) ?? 0;
  });
  return { label, counts, photoCount: uniquePhotoIds.size, hotspotCount };
};
